import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ContentDocument, ContentViewModel } from "../API/Models";
import type { SiteSettings as SiteSettingsModel } from "../types/layout";
import { client } from "../Clients";
import {
	FiAlertCircle,
	FiCheckCircle,
	FiEye,
	FiRefreshCcw,
	FiSave,
	FiUpload
} from "react-icons/fi";
import DeploymentStatusPanel from "../Components/DeploymentStatusPanel";
import UploadJsonModal from "../Components/UploadJsonModal";
import LayoutPreviewModal from "../Components/LayoutEditor/LayoutPreviewModal";
import ContentHistoryPanel from "../Components/ContentHistory/ContentHistoryPanel";
import LayoutPreviewStrip from "../Components/LayoutEditor/LayoutPreviewStrip";
import {
	buildFooterData,
	buildHeaderData,
	buildLanguageOptions
} from "../Components/LayoutEditor/previewUtils";
import {
	buildLayoutPayloadFromSettings,
	createEmptySettings,
	deepCloneLayout,
	layoutPayloadsEqual,
	parseLayoutJson,
	sanitizeLayoutSettings
} from "../Components/LayoutEditor/utils";
import { LayoutSchemaEditor, setLayoutValue } from "../Components/LayoutEditor/LayoutSchemaEditor";
import type { IApplicationState } from "../Redux/Reducers";
import { fetchContentTypes } from "../Redux/Slice/ContentTypesSlice";
import { usePageHeader } from "../hooks/usePageHeader";
import { showSuccessModal } from "../Helper/showSuccessModal";

type DeploymentState = "pending" | "needs-deploy" | "up-to-date";

type DeploymentStatus = {
	state: DeploymentState;
	deployedAt: string | null;
};

const DEFAULT_LANGUAGE = "fr";
const LAYOUT_CONTENT_TYPE = "layout";

const camelize = (key: string) => (key ? key.charAt(0).toLowerCase() + key.slice(1) : key);

// Some schemas come back with PascalCase property keys (from .NET reflection) while
// the stored payload uses camelCase. Normalize keys so the form can read/write
// the actual data fields.
const normalizeSchemaKeys = (schema: any): any => {
	if (!schema || typeof schema !== "object") return schema;

	const cloned: any = Array.isArray(schema) ? [...schema] : { ...schema };

	if (schema.properties && typeof schema.properties === "object") {
		const nextProps: Record<string, any> = {};
		for (const [key, value] of Object.entries(schema.properties)) {
			const normalizedKey = /^[A-Z]/.test(key) ? camelize(key) : key;
			nextProps[normalizedKey] = normalizeSchemaKeys(value);
		}
		cloned.properties = nextProps;
	}

	if (schema.items) {
		cloned.items = normalizeSchemaKeys(schema.items);
	}

	return cloned;
};

/**
 * Global site-layout editor: the header (navigation) and footer singletons rendered on every public
 * page. Loads the "layout" document plus its preview/production snapshots via
 * GET /api/content/layout/{lang}, saves with PUT on the same route, and deploys through
 * POST /api/deployment/layout/{lang}/deploy/preview (production promotion happens on the
 * Deployment page). The form is schema-driven: field definitions come from the content-type
 * schemas in Redux ("layout", with optional "layout-header"/"layout-footer" overrides), so a
 * backend schema change reshapes the form without touching this file. Only the French locale is
 * edited here. Live header/footer preview strips render the draft with the real site components.
 */
const LayoutManager: React.FC = () => {
	const [view, setView] = useState<ContentViewModel | null>(null);
	const [draft, setDraft] = useState<SiteSettingsModel | null>(null);
	const [previewLocale, setPreviewLocale] = useState<string>(DEFAULT_LANGUAGE);
	const [loadingView, setLoadingView] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const history = useHistory();
	const [success, setSuccess] = useState<string | null>(null);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const dispatch = useDispatch();
	const contentTypesState = useSelector((state: IApplicationState) => state.contentTypes);
	const layoutSchema = contentTypesState.items?.layout?.schema;
	const headerSchemaOverride = contentTypesState.items?.["layout-header"]?.schema;
	const footerSchemaOverride = contentTypesState.items?.["layout-footer"]?.schema;
	const inlineHeaderData = useMemo(() => buildHeaderData(draft?.header), [draft]);
	const inlineFooterData = useMemo(() => buildFooterData(draft?.footer), [draft]);
	const inlineLanguageOptions = useMemo(() => buildLanguageOptions(previewLocale), [previewLocale]);
	const normalizedLayoutSchema = useMemo(() => normalizeSchemaKeys(layoutSchema as any), [layoutSchema]);
	const layoutProperties = (normalizedLayoutSchema as any)?.properties ?? {};
	const headerSchema = useMemo(
		() => normalizeSchemaKeys(headerSchemaOverride ?? (layoutProperties?.header as any)),
		[headerSchemaOverride, layoutProperties]
	);
	const footerSchema = useMemo(
		() => normalizeSchemaKeys(footerSchemaOverride ?? (layoutProperties?.footer as any)),
		[footerSchemaOverride, layoutProperties]
	);

	const loadView = useCallback(
		async (language: string) => {
			setLoadingView(true);
			setError(null);
			setSuccess(null);
			try {
				const response = await client.content.apiContentSchemaKeyLanguageGet(LAYOUT_CONTENT_TYPE, language);
				const viewModel = response.data ?? null;
				setView(viewModel);

				const previewPayload = (viewModel?.preview?.payload as SiteSettingsModel | undefined) ?? null;
				const productionPayload = (viewModel?.production?.payload as SiteSettingsModel | undefined) ?? null;
				const currentPayload = (viewModel?.current?.data as SiteSettingsModel | undefined) ?? null;

				// Prefer the current database draft so we reflect the latest saved state,
				// then fall back to preview, then production.
				const source = currentPayload ?? previewPayload ?? productionPayload ?? createEmptySettings(language);
				const isDisabled = viewModel?.current?.isDisabled ?? source?.isDisabled ?? false;
				const resolvedLocale = (source?.language ?? language ?? DEFAULT_LANGUAGE).toLowerCase();

				setPreviewLocale(resolvedLocale);
				setDraft(sanitizeLayoutSettings({ ...source, isDisabled, language: resolvedLocale }));
				setIsEditing(false);
			} catch (err) {
				console.error(err);
				setError("Unable to load the selected layout.");
				setView(null);
				setPreviewLocale(DEFAULT_LANGUAGE);
				setDraft(createEmptySettings(language));
			} finally {
				setLoadingView(false);
			}
		},
		[]
	);

	useEffect(() => {
		loadView(DEFAULT_LANGUAGE);
	}, [loadView]);

	useEffect(() => {
		if (!contentTypesState.initialized && !contentTypesState.loading) {
			dispatch(fetchContentTypes() as any);
		}
	}, [contentTypesState.initialized, contentTypesState.loading, dispatch]);

	const openUploadModal = useCallback(() => {
		setUploadModalOpen(true);
	}, []);

	const closeUploadModal = useCallback(() => {
		setUploadModalOpen(false);
	}, []);

	const handleApplyUploadedJson = useCallback((parsed: any) => {
		if (!isEditing) return;
		const normalized = sanitizeLayoutSettings(deepCloneLayout(parseLayoutJson(parsed, DEFAULT_LANGUAGE)));
		setDraft(normalized);
		setSuccess("Layout JSON imported. Review and save.");
		setError(null);
		closeUploadModal();
	}, [closeUploadModal, isEditing]);

	// Hand the mutator a sanitized deep clone: LayoutSchemaEditor writes through arbitrary paths,
	// and letting it touch the previous draft in place would bypass React's change detection (the
	// preview strips would go stale) and leak unsanitized values into the baseline.
	const updateDraft = useCallback((mutator: (next: SiteSettingsModel) => void) => {
		setDraft((prev) => {
			if (!prev) return prev;
			const next = sanitizeLayoutSettings(deepCloneLayout(prev));
			mutator(next);
			return next;
		});
		setSuccess(null);
		setError(null);
	}, []);

	const handleSchemaChange = useCallback(
		(path: Array<string | number>, value: any) => {
			updateDraft((next) => {
				setLayoutValue(next, path, value);
			});
		},
		[updateDraft]
	);

	const handleSave = useCallback(async () => {
		if (!draft) return;
		setSaving(true);
		setError(null);
		setSuccess(null);
		try {
			const payload: ContentDocument = {
				contentType: LAYOUT_CONTENT_TYPE,
				language: DEFAULT_LANGUAGE,
				isDisabled: draft.isDisabled ?? false,
				isDeleted: draft.isDeleted ?? false,
				data: sanitizeLayoutSettings(draft)
			};

			// slug query parameter is only required for page schema; layout does not use it.
			const response = await client.content.apiContentSchemaKeyLanguagePut(
				LAYOUT_CONTENT_TYPE,
				DEFAULT_LANGUAGE,
				undefined,
				payload
			);
			await loadView(DEFAULT_LANGUAGE);
			const successMessage = "Layout saved successfully.";
			setSuccess(successMessage);
			// L'identifiant est lu sur la reponse du PUT : le rechargement ne le remonte pas ici.
			const savedId = response.data?.current?.id ?? null;
			showSuccessModal({
				title: "Layout saved",
				message: successMessage,
				// Pour qui detient le droit : enregistrer et mettre en production en un geste.
				publish: savedId
					? {
							contentId: savedId,
							contentType: LAYOUT_CONTENT_TYPE,
							onPublished: () => void loadView(DEFAULT_LANGUAGE)
						}
					: undefined
			});
			setIsEditing(false);
		} catch (err) {
			console.error(err);
			setError("Unable to save layout. Please review your inputs and try again.");
		} finally {
			setSaving(false);
		}
	}, [draft, client, loadView, setError, setSuccess]);

	const handleStartEdit = useCallback(() => {
		setIsEditing(true);
		setError(null);
		setSuccess(null);
	}, [setError, setSuccess]);

	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
		setError(null);
		setSuccess(null);
		loadView(DEFAULT_LANGUAGE);
	}, [loadView, setError, setSuccess]);

	const canEdit = isEditing && !loadingView;

	const currentPayload = useMemo(() => buildLayoutPayloadFromSettings(draft), [draft]);
	const previewPayload = useMemo(() => {
		if (!view?.preview?.exists || !view.preview?.payload) return null;
		const source = view.preview.payload as SiteSettingsModel;
		return buildLayoutPayloadFromSettings(sanitizeLayoutSettings(source));
	}, [view?.preview]);

	const productionPayload = useMemo(() => {
		if (!view?.production?.exists || !view.production?.payload) return null;
		const source = view.production.payload as SiteSettingsModel;
		return buildLayoutPayloadFromSettings(sanitizeLayoutSettings(source));
	}, [view?.production]);

	// Deployment freshness is derived by comparing canonical payloads, not timestamps: a re-save
	// that produces byte-identical layout output stays "up-to-date", and cosmetic differences the
	// sanitizer strips never show as pending work. Production is compared against the PREVIEW
	// payload (not the draft) because promotion always flows preview → production.
	const previewStatus: DeploymentStatus = useMemo(() => {
		if (!view?.preview?.exists || !previewPayload) {
			return { state: "pending", deployedAt: view?.preview?.deployedAt ?? null };
		}
		if (!currentPayload) {
			return { state: "pending", deployedAt: view.preview.deployedAt ?? null };
		}
		return {
			state: layoutPayloadsEqual(currentPayload, previewPayload) ? "up-to-date" : "needs-deploy",
			deployedAt: view.preview.deployedAt ?? null
		};
	}, [view?.preview, previewPayload, currentPayload]);

	const productionStatus: DeploymentStatus = useMemo(() => {
		if (!view?.production?.exists || !productionPayload) {
			return { state: "pending", deployedAt: view?.production?.deployedAt ?? null };
		}
		if (!view.preview?.exists || !previewPayload) {
			return { state: "needs-deploy", deployedAt: view.production.deployedAt ?? null };
		}
		return {
			state: layoutPayloadsEqual(previewPayload, productionPayload) ? "up-to-date" : "needs-deploy",
			deployedAt: view.production.deployedAt ?? null
		};
	}, [view?.production, view?.preview, previewPayload, productionPayload]);

	const modalPayload = previewPayload ?? currentPayload;

	const headerActions = useMemo(
		() => (
			<>
				<button
					type="button"
					onClick={() => setPreviewOpen(true)}
					disabled={loadingView || !modalPayload}
					className="inline-flex items-center rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiEye className="mr-2" /> Preview
				</button>
				<button
					type="button"
					onClick={() => {
						setSuccess(null);
						setError(null);
						openUploadModal();
					}}
					disabled={!isEditing || loadingView}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiUpload className="mr-2" /> Upload JSON
				</button>
				{isEditing ? (
					<>
						<button
							type="button"
							onClick={handleSave}
							disabled={saving || loadingView || !draft}
							className="inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
						>
							{saving ? (
								<>
									<FiRefreshCcw className="mr-2 animate-spin" /> Saving…
								</>
							) : (
								<>
									<FiSave className="mr-2" /> Save changes
								</>
							)}
						</button>
						<button
							type="button"
							onClick={handleCancelEdit}
							disabled={loadingView}
							className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							Cancel
						</button>
					</>
				) : (
					<button
						type="button"
						onClick={handleStartEdit}
						disabled={loadingView}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						Enable editing
					</button>
				)}
			</>
		),
		[
			draft,
			handleCancelEdit,
			handleSave,
			isEditing,
			loadingView,
			openUploadModal,
			saving,
			setError,
			setSuccess
		]
	);

	usePageHeader({
		title: "Layout",
		actions: headerActions
	});

	const deploymentCards = [
		{
			label: "Preview environment",
			status: previewStatus.state,
			deployedAt: previewStatus.deployedAt,
			action: (
				<span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" title="La preview se met à jour automatiquement après chaque enregistrement">
					Mise à jour automatique
				</span>
			)
		},
		{
			label: "Production environment",
			status: productionStatus.state,
			deployedAt: productionStatus.deployedAt,
			action: (
				<button
					type="button"
					onClick={() => history.push("/deployment")}
					className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-4 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
				>
					Production
				</button>
			)
		}
	];

	return (
		<>
			<div className="w-full">
				
				<div className="px-4 sm:px-6 pt-4 pb-6 space-y-5">
					<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
						<p className="text-sm text-slate-600">
							Edit the navigation and footer layout for the French locale. Use the edit actions to reorder items : the preview
							updates itself after every save.
						</p>
					</div>
					{error && (
						<div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
							<FiAlertCircle /> {error}
						</div>
					)}
					{success && (
						<div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
							<FiCheckCircle /> {success}
						</div>
					)}

					<DeploymentStatusPanel cards={deploymentCards} />

				{loadingView || !draft ? (
					<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center text-slate-500">
						Loading layout…
					</div>
				) : (
					<div className="space-y-12">
						<div className="grid layout-editor-grid gap-6 xl:gap-8">
							<div className="space-y-4">
								<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
									<label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
										<input
											type="checkbox"
											className="h-4 w-4 rounded border-slate-300 text-slate-900"
											checked={draft.isDisabled ?? false}
											disabled={!canEdit}
											onChange={(event) => handleSchemaChange(["isDisabled"], event.target.checked)}
										/>
										{(layoutProperties?.isDisabled as any)?.title ?? "Disable locale"}
									</label>
								</div>
								{layoutSchema && headerSchema ? (
									<LayoutSchemaEditor
										title={headerSchema.title ?? "Header content"}
										description="Header content"
										schema={headerSchema}
										value={draft.header ?? {}}
										path={["header"]}
										onChange={handleSchemaChange}
										editable={canEdit}
									/>
								) : (
									<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
										Schema not loaded yet.
									</div>
								)}
							</div>
							<aside className="w-full">
								<div className="sticky top-0 max-h-[calc(100vh-0.5rem)] space-y-3 overflow-y-auto pr-1 ">
									<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
										Header preview
									</div>
									<div className="hidden show-1840-block">
										<LayoutPreviewStrip
											mode="header"
											headerData={inlineHeaderData}
											language={previewLocale}
											languageOptions={inlineLanguageOptions}
											defaultLanguageCode={previewLocale}
											viewport="desktop"
											title="Header preview"
											description="Desktop view"
										/>
									</div>
									<LayoutPreviewStrip
										mode="header"
										headerData={inlineHeaderData}
										language={previewLocale}
										languageOptions={inlineLanguageOptions}
										defaultLanguageCode={previewLocale}
										viewport="mobile"
										title="Header preview"
										description="Mobile view"
									/>
								
								</div>
							</aside>
						</div>

						<div className="grid layout-editor-grid gap-6 xl:gap-8 ">
							<div>
								{layoutSchema && footerSchema ? (
									<LayoutSchemaEditor
										title={footerSchema.title ?? "Footer zones"}
										description="Footer zones"
										schema={footerSchema}
										value={draft.footer ?? {}}
										path={["footer"]}
										onChange={handleSchemaChange}
										editable={canEdit}
									/>
								) : (
									<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
										Schema not loaded yet.
									</div>
								)}
							</div>
							<aside className="w-full">
								<div className="sticky top-0 max-h-[calc(100vh-0.5rem)] space-y-3 overflow-y-auto pr-1">
									<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
										Footer preview
									</div>
									<div className="hidden show-1840-block">
										<LayoutPreviewStrip
											mode="footer"
											headerData={inlineHeaderData}
											footerData={inlineFooterData}
											language={previewLocale}
											languageOptions={inlineLanguageOptions}
											defaultLanguageCode={previewLocale}
											viewport="desktop"
											title="Footer preview"
											description="Desktop view"
										/>
									</div>
									<LayoutPreviewStrip
										mode="footer"
										headerData={inlineHeaderData}
										footerData={inlineFooterData}
										language={previewLocale}
										languageOptions={inlineLanguageOptions}
										defaultLanguageCode={previewLocale}
										viewport="mobile"
										title="Footer preview"
										description="Mobile view"
									/>
									
								</div>
							</aside>
						</div>
					</div>
				)}
				{!loadingView && <ContentHistoryPanel contentId={view?.current?.id ?? null} />}
			</div>
		</div>

		<LayoutPreviewModal
			show={previewOpen}
			onClose={() => setPreviewOpen(false)}
			disabled={draft?.isDisabled ?? false}
			initialLocale={previewLocale}
			payloadOverride={draft ? buildLayoutPayloadFromSettings(sanitizeLayoutSettings(draft)) : null}
			localeOverride={previewLocale}
			languageOptionsOverride={inlineLanguageOptions}
			headerOverride={inlineHeaderData}
			footerOverride={inlineFooterData}
		/>

		<UploadJsonModal
			show={uploadModalOpen}
			onHide={closeUploadModal}
			onUpload={handleApplyUploadedJson}
			title="Import layout JSON"
			description="The JSON should follow the public layout schema (header and footer). Any unsupported fields (like header images) are ignored automatically."
		/>
		</>
	);
};

export default LayoutManager;
